import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { Formik } from "formik";
import { Form as AntForm, Input, Button, message } from "antd";
import {
  postFeatureRequest,
  putFeatureRequest,
  getFeatureRequests,
  clearToast,
} from "../../../_actions/user_actions";

const formItemLayout = {
  labelCol: { xs: { span: 24 }, sm: { span: 6 } },
  wrapperCol: { xs: { span: 24 }, sm: { span: 18 } },
};

const FIXED_FORM_WIDTH = 420;
const GAP = 60; // space between fixed form and list
const HEADER_HEIGHT = 60;

function FeatureRequestPage({ data: props }) {
  const dispatch = useDispatch();
  const userID = props.user?.userData?.email || "guest";
  const existingRequests = props.user?.featureRequests || [];

  // Local UI state
  const [loadingVotes, setLoadingVotes] = useState({});
  const [search, setSearch] = useState("");

  // Ant Design form instance
  const [form] = AntForm.useForm();

  /* ------------------------------ TOASTS ------------------------------ */
  useEffect(() => {
    const toast = props?.user?.toast;
    if (!toast) return;
    if (toast !== "Bug Failed" && toast !== "Bug Success") {
      toast.includes("Failed") ? message.error(toast) : message.success(toast);
      dispatch(clearToast());
    }
  }, [props?.user?.toast, dispatch]);

  /* --------------------------- INITIAL DATA --------------------------- */
  useEffect(() => {
    dispatch(getFeatureRequests());
  }, [dispatch]);

  /* --------------------------- VOTING LOGIC --------------------------- */
  const handleVoteToggle = async (request) => {
    setLoadingVotes((prev) => ({ ...prev, [request.id]: true }));
    try {
      await dispatch(putFeatureRequest({ id: request.id, userID }));
      await dispatch(getFeatureRequests());
    } finally {
      setLoadingVotes((prev) => ({ ...prev, [request.id]: false }));
    }
  };

  const hasVoted = (request) => {
    try {
      const voters = Array.isArray(request.voters)
        ? request.voters
        : JSON.parse(request.voters || "[]");
      return voters.includes(userID);
    } catch {
      return false;
    }
  };

  /* ----------------------- FILTERED REQUEST LIST ---------------------- */
  const filteredRequests = existingRequests.filter((req) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      req.title.toLowerCase().includes(q) ||
      req.description.toLowerCase().includes(q)
    );
  });

  /* ------------------------------ RENDER ------------------------------ */
  return (
    <div className="body" style={{ padding: "0 24px", paddingTop: HEADER_HEIGHT + 20 }}>

      {/* Floating form on the left */}
      <div
        style={{
          position: "fixed",
          top: HEADER_HEIGHT + 40,
          left: 24,
          width: FIXED_FORM_WIDTH,
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: 24,
          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          background: "#fff",
          zIndex: 999,
        }}
      >
        <h2>New Feature Request</h2>
        <Formik
          initialValues={{ title: "", description: "" }}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            const res = await dispatch(postFeatureRequest({ ...values, userID }));
            setSubmitting(false);
            if (res.payload?.error) {
              message.error("Failed to submit: " + res.payload.error);
            } else {
              message.success("Feature request submitted!");
              resetForm();
              form.resetFields();
              dispatch(getFeatureRequests());
            }
          }}
        >
          {(formik) => (
            <AntForm {...formItemLayout} form={form} onFinish={formik.handleSubmit}>
              <AntForm.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: "Please enter a title" }]}
              >
                <Input
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  disabled={formik.isSubmitting}
                />
              </AntForm.Item>

              <AntForm.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: "Please enter a description" }]}
              >
                <Input.TextArea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  rows={6}
                  disabled={formik.isSubmitting}
                />
              </AntForm.Item>

              <AntForm.Item wrapperCol={{ offset: 6, span: 18 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                  block
                >
                  Submit Request
                </Button>
              </AntForm.Item>
            </AntForm>
          )}
        </Formik>
      </div>

      {/* Right‑hand column of requests */}
      <div
        style={{
          marginLeft: FIXED_FORM_WIDTH + GAP,
          paddingRight: 24,
          width: `calc(100% - ${FIXED_FORM_WIDTH + GAP}px)` /* take remaining */,
          minWidth: "66%",
        }}
      >
        {/* Sticky heading + search row */}
        <div
          style={{
            position: "sticky",
            top: HEADER_HEIGHT + 40,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fff",
            padding: "8px 0",
            marginBottom: 16,
            zIndex: 998,
            gap: 16,
          }}
        >
          <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>Existing Requests</h2>
          <Input.Search
            placeholder="Search requests"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
          />
        </div>

        <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
          {filteredRequests.map((req) => {
            const loading = !!loadingVotes[req.id];
            const voted = hasVoted(req);
            return (
              <li
                key={req.id}
                style={{
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 16,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  background: "#fff",
                }}
              >
                <strong>{req.title}</strong>
                <p style={{ margin: "4px 0 12px" }}>{req.description}</p>
                <Button
                  size="small"
                  type={voted ? "danger" : "primary"}
                  onClick={() => handleVoteToggle(req)}
                  loading={loading}
                  disabled={loading}
                >
                  {voted ? "Remove Upvote" : "Upvote"} ( {req.votes || 0} )
                </Button>
              </li>
            );
          })}
          {filteredRequests.length === 0 && <p>No requests match your search.</p>}
        </ul>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  data: state,
});

export default withRouter(connect(mapStateToProps)(FeatureRequestPage));
